import { ReactNode } from 'react';

import { FaiTabsEnum } from '@app-types/home-fai';

import { type FaqSlugType, type LegalsSlugType, PAGE_SLUG, partials } from '@helpers/partials';

export type ProviderHomeSsiBlockConfig = {
    url: string;
};

export type ProviderHomeFaqConfig = {
    title: ReactNode;
    titleAlt: string;
    url: FaqSlugType;
    wrapper?: {
        rowClassName?: string;
    };
};

export type ProviderHomeLegalsConfig = {
    id: string;
    url: LegalsSlugType;
};

export type ProviderHomeTabConfig = {
    ssiBlocks: ProviderHomeSsiBlockConfig[];
    faq: ProviderHomeFaqConfig;
    legals: ProviderHomeLegalsConfig;
};

const defaultSsiBlockConfigs: ProviderHomeSsiBlockConfig[] = [
    { url: partials.MARKETING.FAI_EXTRA_BBOX_PACK },
    { url: partials.MARKETING.FAI_WIFI },
    { url: partials.MARKETING.FAI_COMMITMENTS },
    { url: partials.MARKETING.FAI_CONTENT },
    { url: partials.MARKETING.FAI_PERFORMANCE },
    { url: partials.MARKETING.FAI_APP_BOUYGUES },
];

const defaultLegalsConfigs: ProviderHomeLegalsConfig = { id: 'fai-home-legals', url: PAGE_SLUG.FAI_HOME };
const box5gLegals: ProviderHomeLegalsConfig = { id: '', url: PAGE_SLUG.BOX_5G };

const defaultFaqConfig: ProviderHomeFaqConfig = {
    title: 'Des questions sur nos box internet ?\nC’est par ici !',
    titleAlt: 'Des questions sur nos box internet ? C’est par ici !',
    url: PAGE_SLUG.FAI_HOME,
};

const box5gFaqConfig: ProviderHomeFaqConfig = {
    title: 'Des questions sur notre 5G box ?\nOn vous répond !',
    titleAlt: 'Des questions sur notre 5G box ? On vous répond !',
    url: PAGE_SLUG.BOX5G,
};

const pureByouFaqConfig: ProviderHomeFaqConfig = {
    title: 'Vos questions fréquentes\nsur les offres B&YOU',
    titleAlt: 'Vos questions fréquentes sur les offres B&YOU',
    url: 'byou-fai',
    wrapper: {
        rowClassName: 'byou',
    },
};

const defaultTabConfig: ProviderHomeTabConfig = {
    ssiBlocks: defaultSsiBlockConfigs,
    faq: defaultFaqConfig,
    legals: defaultLegalsConfigs,
};

const endOfLineConfig: ProviderHomeTabConfig = {
    ssiBlocks: [
        { url: partials.MARKETING.FAI_COMMITMENTS_WIFI5 },
        { url: partials.MARKETING.FAI_CONTENT },
        { url: partials.MARKETING.FAI_PERFORMANCE },
        { url: partials.MARKETING.FAI_APP_BOUYGUES },
    ],
    faq: defaultFaqConfig,
    legals: defaultLegalsConfigs,
};

export const providerHomeTabConfig: Record<FaiTabsEnum, ProviderHomeTabConfig> = {
    [FaiTabsEnum.PURE_BYOU]: {
        ssiBlocks: [{ url: partials.MARKETING.FAI_PRESENTATION_BYOU }],
        faq: pureByouFaqConfig,
        legals: defaultLegalsConfigs,
    },
    [FaiTabsEnum.BOX_INTERNET]: defaultTabConfig,
    [FaiTabsEnum.BOX_5G]: {
        ssiBlocks: [
            { url: partials.MARKETING.FAI_WIFI },
            { url: partials.MARKETING.FAI_COMMITMENTS },
            { url: partials.MARKETING.FAI_CONTENT },
            { url: partials.MARKETING.FAI_PERFORMANCE },
        ],
        faq: box5gFaqConfig,
        legals: box5gLegals,
    },
    [FaiTabsEnum.FIN_DE_SERIE]: endOfLineConfig,
    [FaiTabsEnum.BIG]: defaultTabConfig,
};

export const getProviderHomeTabConfig = (tab: FaiTabsEnum): ProviderHomeTabConfig => {
    return providerHomeTabConfig[tab] ?? defaultTabConfig;
};
